/**
 * @file 見た目 (ライト / ダーク) の選択を保持し、画面へ適用する。
 *
 * @what: 選択は「自動 (端末の設定に従う) / ライト / ダーク」の3つ。自動のときは
 *   端末の設定を読んで light か dark のどちらかに解決し、その結果だけを
 *   `<html data-theme>` に置く。CSS 側はこの属性だけを見る (studyTheme.css)。
 * @why: CSS にメディアクエリと属性の2系統を持たせると、ダークの値が2か所に散って
 *   食い違う。解決を JS 側に寄せると、色の宣言は1か所で済む。最初の描画で白くならない
 *   よう、index.html の先頭に同じ解決を行う短いスクリプトを置いてある (二重管理になるので
 *   キーと既定値を変えるときは両方直す)。
 * @failure: localStorage が使えない環境 (プライベートモード等) では読み書きが例外を
 *   投げるため、握って既定 (自動) に倒す。色そのものは CSS の既定値 (ライト) に戻るだけ。
 */

import { useCallback, useEffect, useState } from "react";

export type ThemeMode = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "berry-buds:study-theme";

/** ブラウザのアドレスバーに出す色 (解決後のテーマごと)。 */
const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: "#1c5cab",
  dark: "#0e1622",
};

const DARK_QUERY = "(prefers-color-scheme: dark)";

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === "system" || value === "light" || value === "dark";
}

export function readThemeMode(): ThemeMode {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(raw) ? raw : "system";
  } catch {
    return "system";
  }
}

export function writeThemeMode(mode: ThemeMode): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // 保存できなくてもその場の切り替えは効くので、そのまま進む
  }
}

export function resolveTheme(mode: ThemeMode, prefersDark: boolean): ResolvedTheme {
  if (mode === "system") return prefersDark ? "dark" : "light";
  return mode;
}

export function applyTheme(resolved: ResolvedTheme): void {
  document.documentElement.dataset.theme = resolved;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = THEME_COLOR[resolved];
}

/** 端末の設定を読む。使えない環境ではライトに倒す。 */
function darkQuery(): MediaQueryList | null {
  try {
    return typeof window.matchMedia === "function" ? window.matchMedia(DARK_QUERY) : null;
  } catch {
    return null;
  }
}

function prefersDarkNow(): boolean {
  return darkQuery()?.matches ?? false;
}

/**
 * 選択を保持し、`<html data-theme>` へ反映する。自動のときは端末の設定の変化にも追従する。
 */
export function useThemeMode(): { mode: ThemeMode; setMode: (next: ThemeMode) => void } {
  const [mode, setModeState] = useState<ThemeMode>(() => readThemeMode());

  useEffect(() => {
    applyTheme(resolveTheme(mode, prefersDarkNow()));
    if (mode !== "system") return;
    const mql = darkQuery();
    if (!mql) return;
    const onChange = (e: MediaQueryListEvent) => applyTheme(resolveTheme("system", e.matches));
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    writeThemeMode(next);
  }, []);

  return { mode, setMode };
}
